import { chatModelFor, isInternalAccount } from '../../utils/chatModelCapabilities';
import React, { createContext, useEffect, useState, useTransition } from 'react';
import { useAuth0, AuthorizationParams } from "@auth0/auth0-react";
import { API_BASE } from '../Constants';
import { ChatMessage } from '../interfaces';
import Pusher from 'pusher-js';
import { CurrentChatInfo } from '../types/CurrentChatInfo';
import { useFeatureFlags } from './FeatureFlagsContext';

export interface childrenProps {
  children: React.ReactNode;
}

export interface ChatControlProviderProps {
  activeChatID: CurrentChatInfo;
  setActiveChatID: (p: CurrentChatInfo) => void;
  modelChat: string;
  setModelChat: (p: string) => void;
  userChats: object[];
  setUserChats: (p: object[]) => void;
  messageHistory: ChatMessage[];
  setMessageHistory: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  loadingHistory: boolean;
  setLoadingHistory: (p: boolean) => void;
  loadingInitialChats: boolean;
  setLoadingInitialChats: (p: boolean) => void;
  pusherObj: Pusher | undefined;
}

const ChatControlContext = createContext<ChatControlProviderProps | null>(null);

export const ChatControlProvider = ({ children }: childrenProps) => {
  const featureFlags = useFeatureFlags();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [activeChatID, setActiveChatID] = useState<CurrentChatInfo>({"chatID": ""});
  const [modelChat, setModelChat] = useState<string>(() => chatModelFor(user?.email));
  const [modelDefaultApplied, setModelDefaultApplied] = useState(false);

  const [userChats, setUserChats] = useState<object[]>([]);
  const [messageHistory, setMessageHistory] = useState<ChatMessage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [loadingInitialChats, setLoadingInitialChats] = useState<boolean>(false);
  const [pusherObj, setPusherObj] = useState<Pusher>();
  const [, startHistoryTransition] = useTransition();
  // The provider mounts before Auth0 finishes loading the user's email. Apply
  // the account's model once: an internal account always chats with Sabiá 5;
  // anyone else starts on the default, then history and Pensamento own it.
  useEffect(() => {
    if (modelDefaultApplied || !isAuthenticated || !user?.email) return;
    if (isInternalAccount(user.email) || !activeChatID.chatID) setModelChat(chatModelFor(user.email));
    setModelDefaultApplied(true);
  }, [isAuthenticated, user?.email, activeChatID.chatID, modelDefaultApplied]);
  

  const providerValue: ChatControlProviderProps = {
    activeChatID,
    setActiveChatID,
    modelChat,
    setModelChat,
    userChats,
    setUserChats,
    messageHistory,
    setMessageHistory,
    loadingHistory,
    setLoadingHistory,
    loadingInitialChats,
    setLoadingInitialChats,
    pusherObj,
  };

  useEffect(() => {
    if (isAuthenticated) {
      const url = API_BASE + "/api/chat/list";
      const fetchchats = async () => {
        setLoadingInitialChats(true);
        try {
          const access_token= await getAccessTokenSilently();

          const response = await fetch(url, {
            headers: {
              Authorization: `Auth ${access_token}`,
            },
            method: 'GET',
            }
            );
          if (!response.ok) {
            return;
          }
          const data = await response.json();
          setUserChats(data["chats"]);
        } catch (error) {
          console.error('Erro ao carregar conversas:', error);
        } finally {
          setLoadingInitialChats(false);
        }
      }
      fetchchats();
    }
  }, [isAuthenticated]);

  // fetch message history for active chat
  useEffect(() => {
    if (isAuthenticated && activeChatID["chatID"] != "") {
      const url = API_BASE + "/api/chat/history/" + activeChatID["chatID"];
      const controller = new AbortController();
      // true quando o setLoadingHistory(false) já foi no MESMO lote (transição)
      // que troca as mensagens — ver abaixo.
      let loadingClearedWithMessages = false;
      const fetchHistory = async () => {
        if (activeChatID["skipUpdate"]){
          return;
        }
        setLoadingHistory(true);
        try {
          const access_token= await getAccessTokenSilently();

          const response = await fetch(url, {
            headers: {
              Authorization: `Auth ${access_token}`,
            },
            method: 'GET',
            signal: controller.signal,
            }
            );
          
          if (response.ok){
            // if there are no messages and the chatid==message[0] chatid skip
            const data = await response.json();

            if (data["messages"].length > 0 || messageHistory.length==0 || (messageHistory[0] as any)["chat_id"]!= activeChatID["chatID"] ){
              // if theere are messages in the server side history, update them
              // if there are no messages locally, update them
              // if the local message chatid is different from the active chatid, update them

              // Process the filenames
              data["messages"].forEach((message: any) => {
                if (message["files"] != null){
                  message["filenames"] = message["files"].map((file: any) => file["filename"]);
                }
              });
              startHistoryTransition(() => {
                setMessageHistory(data["messages"]);
                // Set model from the chat's stored model, or keep current. An
                // internal account stays on Sabiá 5: the backend moves the
                // chat to it on the next message.
                const chatModel = data["model"];
                if (chatModel && !isInternalAccount(user?.email)) {
                  setModelChat(chatModel);
                }
                // O indicador de carregamento desliga JUNTO com a troca das
                // mensagens. Fora da transição ele era urgente e comitava
                // antes: um frame sem o esqueleto e ainda com as mensagens do
                // chat ANTERIOR — o "pisca outro chat" ao trocar de conversa.
                setLoadingHistory(false);
              });
              loadingClearedWithMessages = true;
            }
          }else{
            setMessageHistory([])
          }
        } catch (error: any) {
          // A newer chat switch aborted this fetch — don't clobber the newer
          // chat's history with this stale request's (empty) result.
          if (error?.name === 'AbortError') return;
          setMessageHistory([]);
        } finally {
          // Don't flip the indicator off for a superseded (aborted) fetch — a
          // newer chat's fetch may still be in flight.
          if (!controller.signal.aborted && !loadingClearedWithMessages) setLoadingHistory(false);
        }

      }
      fetchHistory();
      return () => controller.abort();
    }else if (isAuthenticated && activeChatID["chatID"] == ""){
      setMessageHistory([])
    }
  }, [activeChatID]);



  return (
    <ChatControlContext.Provider value={providerValue}>
      {children}
    </ChatControlContext.Provider>
  );
};

export default ChatControlContext;
